// Tidying the shared program list without touching its records. Some entries are an old name of
// another program, a track inside another degree, or a near copy; they stay in the database (old
// results, saved programs and schools' offered lists still name them) but are left out of new
// recommendations and the Add programs window. A school that offers one still sees it.
// The same as riasecLogic's normalizeName, kept here so riasecLogic can import this file
const normalizeName = (name) => String(name || '').trim().toLowerCase();

// Hidden program → the program it duplicates (null: a track or a generic entry with no single twin)
export const MERGED_INTO = {
  'BS in Electronics & Communications Engineering': 'BS in Electronics Engineering', // renamed under RA 9292
  'BS in Medical Technology': 'BS in Medical Laboratory Science',
  'BS in Hotel and Restaurant Management': 'BS in Hospitality Management',
  'BS in International Hospitality Management': 'BS in Hospitality Management',
  'BS in Tourism': 'BS in Tourism Management',
  'BS in Travel Management': 'BS in Tourism Management',
  'BEED in Preschool Education': 'Bachelor in Early Childhood Education',
  'AB in Public Administration': 'BS in Public Administration',
  'AB in Mass Communication': 'AB in Communication',
  'BSBA in Entrepreneurship': 'BS in Entrepreneurship',
  'BS in Entrepreneurial Management': 'BS in Entrepreneurship',
  'BS in Marketing': 'BSBA in Marketing Management',
  'BS in Finance': 'BSBA in Financial Management',
  'BSBA in Banking and Finance': 'BSBA in Financial Management',
  'BSBA in Management Accounting': 'BS in Management Accounting',
  'BSBA in Management': 'BS in Business Administration',
  'AB in Multimedia Arts': 'BS in Multimedia Arts',
  'Bachelor of Theology': 'AB in Theology',
  'Bachelor of Secondary Education': null, // the BSED majors are listed on their own
  'BS in Mobile App Development': 'BS in Information Technology', // usually a BSIT or BSCS track
  'BS in Web Development': 'BS in Information Technology',
  'BS in Digital Marketing': 'BSBA in Marketing Management', // usually a BSBA major
  'BS in E-commerce': 'BS in Business Administration',
  'BS in Computer Technology': 'BS in Information Technology',
  'BSED in Biological Sciences': null, // CHED's BSED major is Science
  'AB in Arts and Humanities': null,
};

// Names as students should read them; the stored name stays the key for offerings, saves and clicks
const DISPLAY_NAMES = {
  'BS in Foreign Services': 'BS in Foreign Service',
  'BS in Material Engineering': 'BS in Materials Engineering',
  'AB in English': 'AB in English Language Studies',
  'BS in Agricultural Engineering': 'BS in Agricultural and Biosystems Engineering',
};

const HIDDEN = new Set(Object.keys(MERGED_INTO).map(normalizeName));

export const isHiddenProgram = (name) => HIDDEN.has(normalizeName(name));

/** The program a hidden entry duplicates, or null. */
export const twinOf = (name) => {
  const key = Object.keys(MERGED_INTO).find((hidden) => normalizeName(hidden) === normalizeName(name));
  return key ? MERGED_INTO[key] : null;
};

/** The name to show for a program. */
export const programLabel = (name) => DISPLAY_NAMES[String(name || '').trim()] || name;

/**
 * The programs to recommend: hidden ones are left out unless the student's school offers them,
 * and then the program they duplicate is left out for that school instead (unless it offers
 * both), so a school's own listing keeps its details and nothing shows twice.
 */
export const visibleCatalog = (courses = [], offeredNames = new Set()) => {
  const offered = (name) => offeredNames.has(normalizeName(name));
  const standIns = new Set(
    Object.entries(MERGED_INTO)
      .filter(([hidden, twin]) => twin && offered(hidden) && !offered(twin))
      .map(([, twin]) => normalizeName(twin)),
  );
  return courses.filter((course) =>
    isHiddenProgram(course.name) ? offered(course.name) : !standIns.has(normalizeName(course.name)),
  );
};
